/**
 * GA4 ecommerce tracking via GTM dataLayer.
 *
 * All events follow the GA4 recommended ecommerce schema.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

import { PRICING } from "./kenoEfficacite";
import { currentCtaSource } from "./ctaSource";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    posthog?: {
      capture?: (event: string, properties?: Record<string, unknown>, options?: Record<string, unknown>) => void;
    };
    __kenoAnalyticsQueue?: [string, Record<string, unknown>][];
  }
}

// --- Helpers ---

function safePush(data: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * Événement PostHog anonyme (visiteurs). Si le chargeur /analytics.js est actif
 * mais n'a pas encore démarré PostHog, l'événement attend dans sa file. Sans
 * chargeur (connecté, suivi désactivé), rien n'est envoyé. Jamais bloquant.
 */
function posthogCapture(
  event: string,
  properties: Record<string, unknown>,
  options?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  try {
    const capture = window.posthog?.capture;
    if (typeof capture === "function") {
      if (options) {
        capture.call(window.posthog, event, properties, options);
      } else {
        capture.call(window.posthog, event, properties);
      }
    } else if (Array.isArray(window.__kenoAnalyticsQueue)) {
      window.__kenoAnalyticsQueue.push([event, properties]);
    }
  } catch {
    /* le suivi ne doit jamais casser l'inscription */
  }
}

/**
 * Compte de test (« +test » avant le @) : le navigateur devient un appareil de
 * test, que le chargeur /analytics.js ne suit plus, même déconnecté.
 */
export function markTestDevice(email: string | null | undefined): void {
  if (typeof window === "undefined" || !email) return;
  const at = email.lastIndexOf("@");
  if (at === -1 || !email.slice(0, at).toLowerCase().includes("+test")) return;
  try {
    window.localStorage.setItem("keno_analytics_off", "1");
  } catch {
    /* stockage bloqué */
  }
}

function clearEcommerce(): void {
  safePush({ ecommerce: null });
}

interface GA4Item {
  item_id: string;
  item_name: string;
  item_category: string;
  item_variant: string;
  price: number;
  quantity: number;
}

export type PlanType = "monthly" | "annual" | "renewal" | "semester";

function buildItem(plan: PlanType): GA4Item {
  const variants: Record<PlanType, { id: string; variant: string; price: number }> = {
    monthly: { id: "kenometric_monthly", variant: "Mensuel", price: PRICING.monthly.amount },
    annual: { id: "kenometric_annual", variant: "Annuel", price: PRICING.annual.amount },
    renewal: { id: "kenometric_renewal", variant: "Renouvellement", price: PRICING.renewal.amount },
    semester: { id: "kenometric_semester", variant: "6 mois", price: PRICING.semester.amount },
  };

  const v = variants[plan];
  return {
    item_id: v.id,
    item_name: "KenoMetric Premium",
    item_category: "Abonnement",
    item_variant: v.variant,
    price: v.price,
    quantity: 1,
  };
}

// Purchase dedup: in-memory + sessionStorage to survive page reload
const PURCHASE_STORAGE_KEY = "ga4_purchase_sent";

function isPurchaseSent(transactionId: string): boolean {
  try {
    const stored = sessionStorage.getItem(PURCHASE_STORAGE_KEY);
    if (stored) {
      const ids: string[] = JSON.parse(stored);
      if (ids.includes(transactionId)) return true;
    }
  } catch {
    // sessionStorage unavailable
  }
  return false;
}

function markPurchaseSent(transactionId: string): void {
  try {
    const stored = sessionStorage.getItem(PURCHASE_STORAGE_KEY);
    const ids: string[] = stored ? JSON.parse(stored) : [];
    ids.push(transactionId);
    sessionStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // sessionStorage unavailable
  }
}

// --- Ecommerce events ---

export function trackViewItemList(plans: PlanType[]): void {
  const items = plans.map((plan, index) => ({ ...buildItem(plan), index }));
  clearEcommerce();
  safePush({
    event: "view_item_list",
    ecommerce: {
      item_list_id: "pricing",
      item_list_name: "Page Tarifs",
      items,
    },
  });
}

export function trackViewItem(plan: PlanType): void {
  const item = buildItem(plan);
  clearEcommerce();
  safePush({
    event: "view_item",
    ecommerce: {
      currency: "EUR",
      value: item.price,
      items: [item],
    },
  });
}

export function trackAddToCart(plan: PlanType): void {
  const item = buildItem(plan);
  clearEcommerce();
  safePush({
    event: "add_to_cart",
    ecommerce: {
      currency: "EUR",
      value: item.price,
      items: [item],
    },
  });
}

// Dedup begin_checkout: même plan refire-able après 2s seulement (couvre le
// cas du double-clic rapide où trackBeginCheckout est appelé avant que le
// bouton ne se désactive via createCheckout.isPending).
const beginCheckoutLastFire = new Map<PlanType, number>();
const BEGIN_CHECKOUT_DEDUP_MS = 2000;

export function trackBeginCheckout(plan: PlanType): void {
  const now = Date.now();
  const last = beginCheckoutLastFire.get(plan) ?? 0;
  if (now - last < BEGIN_CHECKOUT_DEDUP_MS) return;
  beginCheckoutLastFire.set(plan, now);

  const item = buildItem(plan);
  clearEcommerce();
  safePush({
    event: "begin_checkout",
    ecommerce: {
      currency: "EUR",
      value: item.price,
      items: [item],
    },
  });
}

export interface TransactionData {
  id: string;
  amount: number;
  currency: string;
  productType: string;
  coupon: string | null;
  discount: number;
}

export function trackPurchase(transaction: TransactionData): void {
  if (isPurchaseSent(transaction.id)) return;
  markPurchaseSent(transaction.id);

  const plan: PlanType = transaction.productType === "monthly"
    ? "monthly"
    : transaction.productType === "renewal"
      ? "renewal"
      : transaction.productType === "semester"
        ? "semester"
        : "annual";

  const item = buildItem(plan);

  clearEcommerce();
  safePush({
    event: "purchase",
    ecommerce: {
      transaction_id: transaction.id,
      value: transaction.amount,
      currency: transaction.currency.toUpperCase(),
      coupon: transaction.coupon ?? undefined,
      discount: transaction.discount || undefined,
      items: [item],
    },
  });
}

// --- User events ---

/**
 * Tracker pour le clic sur le CTA d'inscription (avant la soumission magic-link).
 * À distinguer de trackSignUp() qui se déclenche après création effective du compte.
 * Permet de mesurer le funnel landing → inscription → confirmation.
 */
export function trackBeginSignup(method: string = "email", source?: string): void {
  safePush({ event: "begin_signup", method, source: source ?? "inscription" });
  posthogCapture("signup_started", withCtaSource({ method, source: source ?? "inscription" }));
}

/** Ajoute l'encart d'inscription d'origine de la visite (helpers/ctaSource.ts), s'il existe. */
function withCtaSource(properties: Record<string, unknown>): Record<string, unknown> {
  const cta = typeof window === "undefined" ? null : currentCtaSource();
  return cta ? { ...properties, cta_source: cta } : properties;
}

/**
 * Compte créé. Le navigateur n'envoie plus signup_completed à PostHog : le serveur
 * l'envoie seul (UserSignupAnalyticsListener), avec l'encart d'origine (cta_source),
 * pour ne plus compter deux fois chaque inscription. `outil` est gardé pour l'appel
 * existant ; l'outil d'un mur arrive au serveur par cta_source (« mur_<outil> »).
 */
export function trackSignUp(method: string = "email", _outil?: string | null): void {
  safePush({ event: "sign_up", method });
  trackActivationFunnel("signup");
  trackTrialStarted();
}

/**
 * Mur d'inscription affiché pour un outil réservé (refonte, section 5 ter, point 4).
 * voie : « menu » si le visiteur vient de la fenêtre membres du menu (paramètre
 * via=menu), « direct » sinon (lien partagé, Google, nouvel onglet).
 */
export function trackAccountWallHit(outil: string, voie: "direct" | "menu"): void {
  safePush({ event: "account_wall_hit", outil, voie });
  posthogCapture("account_wall_hit", { outil, voie });
}

export function trackLogin(method: string = "password"): void {
  safePush({ event: "login", method });
}

/**
 * Tracker pour le clic sur "Se connecter" (avant succès/échec) : permet de
 * mesurer le funnel entrée page login → tentative → succès, et calculer
 * le taux d'échec (login_attempt vs login).
 */
export function trackLoginAttempt(method: string = "password"): void {
  safePush({ event: "login_attempt", method });
}

/**
 * Clic sur « Continuer avec Google ». Le même bouton sert à s'inscrire et à se
 * connecter : l'intention vient de la page où il est affiché. Sur /inscription,
 * le clic compte comme inscription commencée (un compte existant deviendra une
 * connexion, sans « inscription terminée ») ; sur /login, comme connexion
 * commencée, hors entonnoir d'inscription. Envoi immédiat (sendBeacon) : le clic
 * quitte la page vers Google.
 */
/** Inscription à la newsletter réussie (pied de page, modale). Les pages Twig et pré-rendues passent par window.kenoTrack. */
export function trackNewsletterSubscribed(source: string): void {
  posthogCapture("newsletter_subscribed", { source });
}

export function trackGoogleAuthClick(intent: "signup" | "login"): void {
  const now = { send_instantly: true, transport: "sendBeacon" };
  if (intent === "signup") {
    safePush({ event: "begin_signup", method: "google", source: "inscription" });
    posthogCapture("signup_started", withCtaSource({ method: "google", source: "inscription" }), now);
    return;
  }
  trackLoginAttempt("google");
  posthogCapture("login_started", { method: "google" }, now);
}

/**
 * Tracker pour le clic sur "Recevoir un lien de connexion par email" depuis
 * la page login (différent de begin_signup qui est sur /inscription).
 */
export function trackMagicLinkRequest(source: string = "login"): void {
  safePush({ event: "magic_link_requested", source });
}

// --- Promotion events ---

export function trackViewPromotion(promoId: string, name: string): void {
  clearEcommerce();
  safePush({
    event: "view_promotion",
    ecommerce: {
      promotion_id: promoId,
      promotion_name: name,
    },
  });
}

export function trackSelectPromotion(promoId: string, name: string): void {
  clearEcommerce();
  safePush({
    event: "select_promotion",
    ecommerce: {
      promotion_id: promoId,
      promotion_name: name,
    },
  });
}

// --- Engagement events ---

export function trackEngagement(
  action: string,
  params?: Record<string, string | number | boolean>,
): void {
  safePush({ event: action, ...params });
}

export function trackShare(method: string, contentType: string, itemId?: string): void {
  safePush({ event: "share", method, content_type: contentType, item_id: itemId });
}

// --- Mes grilles events ---

export function trackViewMyDraw(): void {
  safePush({ event: "view_my_draw" });
}

export function trackSetPlayedNumbers(count: number): void {
  safePush({ event: "set_favorite_combination", numbers_count: count });
}

export function trackChooseNotification(channel: string): void {
  safePush({ event: "choose_notification", channel });
}

export function trackEnablePush(): void {
  safePush({ event: "enable_push" });
}

export function trackDismissPush(): void {
  safePush({ event: "dismiss_push" });
}

// --- Premium gate tracking ---

/**
 * Clic sur une fonction réservée au Premium.
 * - Membre connecté : action `premium_feature_blocked` enregistrée sur son compte par
 *   `/_api/track` (journal d'activité admin, PostHog sous son identifiant).
 * - Visiteur : événement PostHog anonyme `premium_gate_hit` (sans cookies, donc sans identité).
 * `feature` et `page` : identifiants courts (minuscules, chiffres, `_`, `-`), seuls textes
 * que le serveur transmet à PostHog.
 */
const gateSlug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-z0-9_-]+/g, "-")
    .slice(0, 60) || "accueil";

export function trackPremiumGateHit(rawFeature: string, rawPage: string): void {
  const feature = gateSlug(rawFeature);
  const page = gateSlug(rawPage);
  safePush({
    event: "premium_gate_hit",
    gate_feature: feature,
    gate_page: page,
  });

  let connected = false;
  try {
    connected = typeof window !== "undefined" && window.localStorage.getItem("has_session") === "true";
  } catch {
    /* localStorage bloqué : traité comme visiteur */
  }

  if (connected) {
    try {
      fetch("/_api/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "premium_feature_blocked",
          metadata: { gate_feature: feature, gate_page: page },
        }),
        credentials: "include",
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* le suivi ne doit jamais bloquer l'interface */
    }
    return;
  }

  posthogCapture("premium_gate_hit", { gate_feature: feature, gate_page: page });
}

export function trackFreeAccountGateHit(feature: string, page: string): void {
  safePush({
    event: "free_account_gate_hit",
    gate_feature: feature,
    gate_page: page,
  });
}

// --- Activation funnel tracking ---

export function trackTrialStarted(): void {
  safePush({ event: "trial_started" });
}

export function trackOnboardingStep(step: string, completed: boolean): void {
  safePush({
    event: "onboarding_step",
    step_name: step,
    step_completed: completed,
  });
}

export function trackActivationFunnel(step: "signup" | "first_action" | "mon_keno_setup" | "premium"): void {
  safePush({
    event: "activation_funnel",
    funnel_step: step,
  });
}

export function trackTrialBonusUnlocked(action: string, daysRemaining: number): void {
  safePush({
    event: "trial_bonus_unlocked",
    bonus_action: action,
    trial_days_remaining: daysRemaining,
  });
}

// --- 404 tracking ---

export function track404(url: string, referrer?: string): void {
  safePush({
    event: "page_not_found",
    page_location: url,
    page_referrer: referrer || document.referrer || undefined,
  });
}

// --- Conversion funnel tracking (visiteur → compte → Premium) ---
//
// Ces events complètent le funnel de conversion : ils mesurent l'exposition aux
// CTA d'inscription, l'atteinte des limites (anonyme et compte gratuit), la
// première action de valeur et l'usage des outils. Toutes les propriétés sont
// optionnelles pour rester rétro-compatibles avec les call sites existants.

export type UserStatus = "anonymous" | "free" | "trial" | "premium";

/** Type d'appareil déduit de la largeur d'écran (best-effort, côté client). */
function deviceType(): "mobile" | "desktop" | undefined {
  if (typeof window === "undefined") return undefined;
  return window.innerWidth <= 768 ? "mobile" : "desktop";
}

/** Un CTA d'inscription est devenu visible pour un visiteur (GTM et PostHog). */
export function trackSignupCtaViewed(source: string, feature?: string): void {
  const properties = { cta_source: source, cta_feature: feature, device_type: deviceType() };
  safePush({ event: "signup_cta_viewed", ...properties });
  posthogCapture("signup_cta_viewed", properties);
}

/** Un visiteur a cliqué sur un CTA d'inscription. `feature` = fonctionnalité déclencheuse (GTM et PostHog). */
export function trackSignupCtaClicked(source: string, feature?: string): void {
  const properties = { cta_source: source, cta_feature: feature, device_type: deviceType() };
  safePush({ event: "signup_cta_clicked", ...properties });
  posthogCapture("signup_cta_clicked", properties);
}

/** Un visiteur anonyme a atteint une limite (contenu gaté nécessitant un compte). */
export function trackAnonymousLimitReached(feature: string, page: string): void {
  safePush({
    event: "anonymous_limit_reached",
    gate_feature: feature,
    gate_page: page,
    device_type: deviceType(),
  });
}

/** Un utilisateur en compte gratuit a atteint un quota (profondeur, nombre d'actions…). */
export function trackFreeLimitReached(feature: string, page: string, quotaRemaining?: number): void {
  safePush({
    event: "free_limit_reached",
    gate_feature: feature,
    gate_page: page,
    quota_remaining: quotaRemaining,
    device_type: deviceType(),
  });
}

/**
 * Première action de valeur réelle après inscription (ex : sauvegarder ses numéros).
 * Idempotent au sein d'une session : n'émet qu'une fois par navigateur.
 */
const FIRST_VALUE_STORAGE_KEY = "ga4_first_value_action_sent";
export function trackFirstValueAction(feature: string): void {
  if (typeof window !== "undefined") {
    try {
      if (window.localStorage.getItem(FIRST_VALUE_STORAGE_KEY)) return;
      window.localStorage.setItem(FIRST_VALUE_STORAGE_KEY, "1");
    } catch {
      // localStorage indisponible (mode privé) : on émet quand même, sans dédup.
    }
  }
  safePush({
    event: "first_value_action",
    value_feature: feature,
  });
  // Conserve la compatibilité avec le funnel d'activation existant.
  trackActivationFunnel("first_action");
}

/** Ouverture d'un outil (page produit). */
export function trackToolView(tool: string, status?: UserStatus): void {
  safePush({ event: "tool_view", tool, user_status: status, device_type: deviceType() });
}

/** Démarrage d'un usage d'outil (ex : lancer une génération, une simulation). */
export function trackToolStarted(tool: string, status?: UserStatus): void {
  safePush({ event: "tool_started", tool, user_status: status });
}

/** Fin d'usage d'un outil avec résultat. */
export function trackToolCompleted(tool: string, meta?: Record<string, unknown>): void {
  safePush({ event: "tool_completed", tool, ...(meta ?? {}) });
}

/** Navigation (1.8.0) : clic dans une zone de navigation (visiteurs anonymes seulement, comme tout PostHog). */
export function trackNavClicked(payload: { rubrique: string; lien: string; emplacement: string }): void {
  posthogCapture("nav_clicked", { ...payload });
}

/** Recherche « Numéro ou date » : le type seulement, jamais le texte saisi. */
export function trackSearchUsed(type: "numero" | "date" | "mot" | "sans_resultat"): void {
  posthogCapture("search_used", { type });
}

/** Fenêtre « Votre essai est terminé » affichée (offre proposée ou non). */
export function trackTrialEndedSeen(offre: "six_mois" | "aucune"): void {
  posthogCapture("trial_ended_seen", { offre });
}

/** Bandeau de fin d'essai vu sur le tableau de bord (D10 : 4 semaines de mesure contre la fenêtre de fin d'essai). */
export function trackTrialBannerSeen(joursRestants: number): void {
  posthogCapture("trial_banner_seen", { jours_restants: joursRestants });
}

/** Clic sur « Mettre à jour ma carte » du bandeau d'impayé (D8), avec la page d'origine. */
export function trackPaymentBannerClick(page: string): void {
  posthogCapture("payment_banner_click", { page });
}

/** Carte « 6 mois pour 19 € » vue sur /tarifs/, avec sa provenance (utm_source ou direct). */
export function trackSixMonthsOfferViewed(source: string): void {
  posthogCapture("six_months_offer_viewed", { source });
}
